import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import styles from "./safetyVideos.module.scss";
import { getEmbedUrl } from "./youtube";
import confirmIcon from "../../../images/safetyVideoConfirm.svg";

// The confirmation card sits on white, so the dark close icon is used here
// rather than the light one from the tracking header.
const CLOSE_ICON =
  "https://d1flzashw70bti.cloudfront.net/original/images/selfHelp/blackCloseBtn_1789716119607.svg";

// The confirmation prompt appears this long after playback starts, and again
// every time it is dismissed without confirming (PRD 11.4).
const CONFIRM_PROMPT_DELAY = 20 * 1000;

const VideoModal = props => {
  const { video, isMandatory, onConfirm, onClose } = props;

  const [showPrompt, setShowPrompt] = useState(false);
  const [promptRound, setPromptRound] = useState(0);

  const [isPlayerLoaded, setIsPlayerLoaded] = useState(false);

  const embedUrl = getEmbedUrl(video.link);

  // The intro and emergency videos reuse this modal back to back, so the
  // loader has to come back for each new video.
  useEffect(() => {
    setIsPlayerLoaded(false);
  }, [embedUrl]);

  useEffect(() => {
    if (!isMandatory || !embedUrl) {
      return undefined;
    }

    const timer = setTimeout(() => setShowPrompt(true), CONFIRM_PROMPT_DELAY);

    return () => clearTimeout(timer);
  }, [isMandatory, embedUrl, promptRound]);

  // Dismissing the prompt leaves the video on screen and re-arms the timer;
  // for a mandatory video "Yes" is the only way out.
  const dismissPrompt = () => {
    setShowPrompt(false);
    setPromptRound(round => round + 1);
  };

  // Portalled to <body> so no tray/page ancestor (stacking context, scroll
  // container, sibling overlay) can sit over the iframe and swallow taps on
  // the player's own controls.
  return ReactDOM.createPortal(
    <div className={styles.videoPage}>
      <div className={styles.videoContent}>
        {embedUrl ? (
          <div className={styles.player}>
            {isPlayerLoaded ? null : (
              <div className={styles.playerLoader}>
                <span />
                <span />
                <span />
                <span />
              </div>
            )}
            <iframe
              title={video.title}
              src={embedUrl}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              onLoad={() => setIsPlayerLoaded(true)}
            />
          </div>
        ) : (
          <div className={styles.playerError}>
            <span className={styles.errorTitle}>
              This video could not be loaded
            </span>
            <span className={styles.errorSubtitle}>
              Please contact customer support for assistance.
            </span>
          </div>
        )}

        {isMandatory && !embedUrl ? (
          <span className={styles.mandatoryNote}>
            Watching this video is mandatory before your journey.
          </span>
        ) : null}

        {isMandatory ? null : (
          <div className={styles.skip} onClick={onClose}>
            <span>Skip Video</span>
          </div>
        )}
      </div>

      {showPrompt ? (
        <div className={styles.prompt}>
          <div className={styles.promptIconRow}>
            <span className={styles.promptSpacer} />
            <img
              className={styles.promptIcon}
              src={confirmIcon}
              alt="confirm"
            />
            <img
              className={styles.promptClose}
              src={CLOSE_ICON}
              alt="close"
              onClick={dismissPrompt}
            />
          </div>
          <div className={styles.promptTexts}>
            <span className={styles.promptTitle}>Confirmation</span>
            <span className={styles.promptText}>
              Have you watched this video?
            </span>
          </div>
          {/* Deliberately no "No" — the only alternative to confirming is
              closing the prompt, which leaves the video playing. */}
          <div
            className={styles.promptConfirm}
            onClick={() => onConfirm(video)}
          >
            <span>Yes</span>
          </div>
        </div>
      ) : null}
    </div>,
    document.body
  );
};

export default VideoModal;
